/**
 * Business context for TIC Comms.
 *
 * Manages the currently selected business, provides role information,
 * and persists the selection to localStorage.
 *
 * Enhanced from starter:
 *   - 4-role hierarchy: viewer < agent < admin < owner
 *   - Org color support (business.color)
 *   - Role helper functions for the current business
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import type { Business, UserRole } from '@/types/database'

const STORAGE_KEY = 'tic-selected-business'

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface BusinessState {
  /** The currently selected business, or null for "All Businesses" group view */
  currentBusiness: Business | null
  /** All active businesses the user is a member of */
  businesses: Business[]
  /** Select a business (or null for group view) */
  setCurrentBusiness: (business: Business | null) => void
  /** True when viewing all businesses (no specific business selected) */
  isGroupView: boolean
  /** The user's role in the currently selected business */
  currentRole: UserRole | null
  /** Helper: get the user's role for any business by ID */
  getRoleForBusiness: (businessId: string) => UserRole | null
  /** Helper: get a business object by ID */
  getBusinessById: (businessId: string) => Business | null
  /** Whether the user is admin or owner in the current context */
  isAdminOrOwner: boolean
  /** Whether the user can reply (agent+) in the current context */
  canReply: boolean
}

const BusinessContext = createContext<BusinessState | undefined>(undefined)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ROLE_LEVELS: Record<UserRole, number> = {
  viewer: 1,
  agent: 2,
  admin: 3,
  owner: 4,
}

function readStoredBusinessId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function BusinessProvider({ children }: { children: ReactNode }) {
  const { memberships, membershipsLoading } = useAuth()
  const [currentBusiness, setCurrentBusinessRaw] = useState<Business | null>(null)

  // Active businesses only
  const businesses = useMemo(
    () => memberships.map((m) => m.business).filter((b) => b.status === 'active'),
    [memberships],
  )

  // Persist selection to localStorage
  const setCurrentBusiness = useCallback((business: Business | null) => {
    setCurrentBusinessRaw(business)
    try {
      if (business) {
        localStorage.setItem(STORAGE_KEY, business.id)
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch { /* localStorage unavailable */ }
  }, [])

  // Auto-select: restore from localStorage, or pick sole business
  useEffect(() => {
    if (membershipsLoading || businesses.length === 0) return
    // Don't override if already selected and still valid
    if (currentBusiness && businesses.some((b) => b.id === currentBusiness.id)) return

    const storedId = readStoredBusinessId()
    const restored = storedId ? businesses.find((b) => b.id === storedId) : null

    if (restored) {
      setCurrentBusinessRaw(restored)
    } else if (businesses.length === 1) {
      // Auto-select sole business
      setCurrentBusinessRaw(businesses[0])
      try { localStorage.setItem(STORAGE_KEY, businesses[0].id) } catch { /* */ }
    }
  }, [membershipsLoading, businesses, currentBusiness])

  // Derived state
  const isGroupView = currentBusiness === null

  const getRoleForBusiness = useCallback(
    (businessId: string): UserRole | null => {
      const membership = memberships.find((m) => m.business_id === businessId)
      return (membership?.role as UserRole) ?? null
    },
    [memberships],
  )

  const getBusinessById = useCallback(
    (businessId: string): Business | null => {
      return businesses.find((b) => b.id === businessId) ?? null
    },
    [businesses],
  )

  // Current role: specific business role, or highest role across all businesses
  const currentRole: UserRole | null = useMemo(() => {
    if (currentBusiness) {
      return getRoleForBusiness(currentBusiness.id)
    }
    // Group view: highest role across all memberships
    if (memberships.length === 0) return null
    let highest: UserRole = 'viewer'
    for (const m of memberships) {
      const role = m.role as UserRole
      if ((ROLE_LEVELS[role] ?? 0) > (ROLE_LEVELS[highest] ?? 0)) {
        highest = role
      }
    }
    return highest
  }, [currentBusiness, memberships, getRoleForBusiness])

  const isAdminOrOwner = !!currentRole && (ROLE_LEVELS[currentRole] ?? 0) >= ROLE_LEVELS.admin
  const canReply = !!currentRole && (ROLE_LEVELS[currentRole] ?? 0) >= ROLE_LEVELS.agent

  return (
    <BusinessContext.Provider
      value={{
        currentBusiness,
        businesses,
        setCurrentBusiness,
        isGroupView,
        currentRole,
        getRoleForBusiness,
        getBusinessById,
        isAdminOrOwner,
        canReply,
      }}
    >
      {children}
    </BusinessContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useBusiness() {
  const context = useContext(BusinessContext)
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider')
  }
  return context
}
